/**
 * DateRangePicker Component
 * 
 * Date range selector with preset options and custom date selection.
 */

import { useState, useRef, useEffect } from 'react';
import './DateRangePicker.css';

export interface DateRange {
  startDate: string;
  endDate: string;
  label: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const PRESET_RANGES: DateRange[] = [
  {
    label: 'Last 7 days',
    startDate: getDateOffset(-7),
    endDate: getDateOffset(0),
  },
  {
    label: 'Last 30 days',
    startDate: getDateOffset(-30),
    endDate: getDateOffset(0),
  },
  {
    label: 'Last 90 days',
    startDate: getDateOffset(-90),
    endDate: getDateOffset(0),
  },
  {
    label: 'This month',
    startDate: getFirstDayOfMonth(),
    endDate: getDateOffset(0),
  },
  {
    label: 'Last month',
    startDate: getFirstDayOfLastMonth(),
    endDate: getLastDayOfLastMonth(),
  },
];

function getDateOffset(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

function getFirstDayOfMonth(): string {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split('T')[0];
}

function getFirstDayOfLastMonth(): string {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  date.setDate(1);
  return date.toISOString().split('T')[0];
}

function getLastDayOfLastMonth(): string {
  const date = new Date();
  date.setDate(0);
  return date.toISOString().split('T')[0];
}

export function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(value.startDate);
  const [customEnd, setCustomEnd] = useState(value.endDate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetSelect = (preset: DateRange) => {
    onChange(preset);
    setIsOpen(false);
    setShowCustom(false);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd && customStart <= customEnd) {
      onChange({
        label: 'Custom',
        startDate: customStart,
        endDate: customEnd,
      });
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  return (
    <div className="date-range-picker" ref={dropdownRef}>
      <button
        className="date-range-trigger"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="calendar-icon">📅</span>
        <span className="date-range-label">
          {value.label !== 'Custom' 
            ? value.label 
            : `${formatDateDisplay(value.startDate)} - ${formatDateDisplay(value.endDate)}`
          }
        </span>
        <span className="chevron-icon">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="date-range-dropdown">
          <div className="preset-options">
            {PRESET_RANGES.map((preset) => (
              <button
                key={preset.label}
                className={`preset-option ${value.label === preset.label ? 'active' : ''}`}
                onClick={() => handlePresetSelect(preset)}
                type="button"
              >
                {preset.label}
              </button>
            ))}
            <button
              className={`preset-option ${showCustom ? 'active' : ''}`}
              onClick={() => setShowCustom(!showCustom)}
              type="button"
            >
              Custom range
            </button>
          </div>

          {showCustom && (
            <div className="custom-range-section">
              <div className="custom-inputs">
                <div className="input-group">
                  <label htmlFor="start-date">Start</label>
                  <input
                    id="start-date"
                    type="date"
                    value={customStart}
                    max={customEnd}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="end-date">End</label>
                  <input
                    id="end-date"
                    type="date"
                    value={customEnd}
                    min={customStart}
                    max={getDateOffset(0)}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              </div>
              <div className="custom-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowCustom(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleCustomApply}
                  disabled={!customStart || !customEnd || customStart > customEnd}
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
